import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { getToken } from "./api";
import { MimiRobot } from "./MimiRobot";
import { EASE } from "@/components/Motion";

const QUICK_PROMPTS = ["How do I add funds?", "Track my order", "Refund policy?", "Who founded BEATKINGSMM?"];

const getSessionId = () => {
  let id = localStorage.getItem("bk_mimi_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("bk_mimi_session", id);
  }
  return id;
};

export default function MimiWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Mimi, your support buddy. Ask me anything about orders, funds or your account!" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", streaming: true }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mimi/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = part.slice(6);
          if (data === "[DONE]") break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) {
              setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { role: "assistant", content: parsed.error } : x)));
            } else if (parsed.delta) {
              setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, content: x.content + parsed.delta } : x)));
            }
          } catch { /* partial chunk */ }
        }
      }
      setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, streaming: false } : x)));
    } catch {
      setMessages((m) => m.map((x, i) => (i === m.length - 1
        ? { role: "assistant", content: "Oops, my wires got crossed — please try again!" } : x)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed bottom-24 left-4 z-50 flex h-[480px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-gold/25 bg-panel/95 shadow-[0_20px_80px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:left-6"
            data-testid="mimi-panel"
          >
            <div className="flex items-center gap-3 border-b border-white/5 bg-gradient-to-r from-gold/[0.12] to-transparent px-5 py-4">
              <span className="relative flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/40 bg-gold/[0.08]">
                <MimiRobot size={34} />
                <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-panel bg-emerald-400" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-neutral-100" data-testid="mimi-title">Mimi AI</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">Support buddy · online</div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close Mimi" data-testid="mimi-close"
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-500 transition-colors hover:text-neutral-200">
                <X size={14} />
              </button>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="mimi-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && (
                    <span className="mb-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-gold/30 bg-gold/[0.08]">
                      <MimiRobot size={20} />
                    </span>
                  )}
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "rounded-br-md bg-gradient-to-br from-goldlight via-gold to-golddark font-medium text-black"
                      : "rounded-bl-md border border-white/10 bg-white/[0.04] text-neutral-200"
                  }`}>
                    {m.content || (m.streaming ? (
                      <span className="flex gap-1 py-1">
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold [animation-delay:0.15s]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold [animation-delay:0.3s]" />
                      </span>
                    ) : "")}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 px-4 pb-3">
                {QUICK_PROMPTS.map((q) => (
                  <button key={q} onClick={() => send(q)} data-testid={`mimi-quick-${q.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="rounded-full border border-gold/25 bg-gold/[0.06] px-3 py-1.5 text-[11px] text-goldlight transition-colors hover:bg-gold/15">
                    {q}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-white/5 p-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Mimi anything…"
                data-testid="mimi-input"
                className="flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-neutral-200 placeholder:text-neutral-600 focus:border-gold focus:outline-none"
              />
              <button type="submit" disabled={busy || !input.trim()} data-testid="mimi-send"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark text-black transition-shadow hover:shadow-[0_4px_20px_rgb(var(--gold)_/_0.4)] disabled:opacity-40">
                <Send size={15} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        data-testid="mimi-float-button"
        aria-label="Chat with Mimi AI"
        className="fixed bottom-6 left-4 z-50 flex items-center gap-2 rounded-full border border-gold/40 bg-panel/90 py-2.5 pl-3 pr-4 shadow-[0_10px_40px_rgb(var(--gold)_/_0.25)] backdrop-blur-xl sm:left-6"
      >
        <motion.span
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="relative flex h-9 w-9 items-center justify-center rounded-full border border-gold/50 bg-gold/[0.1]"
        >
          <MimiRobot size={30} />
          <span className="absolute inset-0 animate-ping rounded-full bg-gold/25" />
        </motion.span>
        <span className="text-xs font-bold tracking-wide text-goldlight">Mimi AI</span>
      </motion.button>
    </>
  );
}
